// src/pages/Futures/OrderForm.js
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box, Typography, TextField, Button, FormControl, InputLabel,
  Select, MenuItem, useTheme, CircularProgress, Dialog,
  DialogTitle, DialogContent, DialogActions, ToggleButton,
  ToggleButtonGroup, styled, LinearProgress, Chip, Slider, InputAdornment
} from '@mui/material';
import { createFutureTrade } from '../../store/Slices/FuturesTradingSlice';
import { fetchFuturesBalances } from '../../store/Slices/assetSlice';

const COLORS = {
  YELLOW: '#F0B90B',
  YELLOW_DARK: '#c99a08',
  GREEN: '#0ECB81',
  RED: '#F6465D',
};

// حاوية أنيقة بستايل Binance
const Card = styled(Box)(({ theme }) => ({
  borderRadius: 14,
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(240,185,11,0.22)' : 'rgba(240,185,11,0.32)'}`,
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(180deg, rgba(27,29,33,0.95), rgba(27,29,33,0.7))'
    : 'linear-gradient(180deg, #ffffff, #ffffff)',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 6px 18px rgba(0,0,0,0.25)'
    : '0 6px 18px rgba(0,0,0,0.06)',
  padding: 14,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: 14,
  letterSpacing: 0.2,
  color: theme.palette.text.primary,
}));

const StyledToggle = styled(ToggleButton)(({ theme, value }) => ({
  border: 'none',
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 10,
  '&.Mui-selected': {
    color: '#fff',
    backgroundColor: value === 'Long' ? COLORS.GREEN : COLORS.RED,
  },
  backgroundColor: theme.palette.mode === 'dark' ? '#2B3139' : '#F2F3F5',
}));

// نمط الحقول: فوكس أصفر + placeholder صغير + إزالة الأسهم لحروف النوع number
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 10,
    backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#1E2329' : '#FAFAFA'),
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.YELLOW },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.YELLOW },
    '& input::placeholder': { fontSize: '0.78rem', opacity: 0.7 },
    // إزالة أسهم الزيادة/النقصان
    '& input[type=number]': { MozAppearance: 'textfield' },
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none', margin: 0,
    },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: COLORS.YELLOW },
};

const selectSx = {
  ...fieldSx,
  '& .MuiSelect-icon': { color: COLORS.YELLOW },
};

// سلايدر الرافعة 1–100
const sliderSx = {
  color: COLORS.YELLOW,
  '& .MuiSlider-thumb': {
    border: `2px solid ${COLORS.YELLOW}`,
    backgroundColor: '#fff',
  },
  '& .MuiSlider-valueLabel': {
    backgroundColor: COLORS.YELLOW,
    color: '#000',
    borderRadius: 6,
    fontWeight: 700,
  },
};

// شارة USDT/Qty أنيقة داخل الـ InputAdornment
const Tag = ({ text }) => (
  <Box
    sx={{
      fontSize: 12, px: 1, py: 0.25, borderRadius: 999,
      bgcolor: 'rgba(240,185,11,0.15)', color: '#a07a05', fontWeight: 700,
    }}
  >
    {text}
  </Box>
);

export default function FuturesOrderForm() {
  const dispatch = useDispatch();
  const theme = useTheme();

  const { selectedCoin } = useSelector((s) => s.futures);
  const { futuresBalances, spotTradingPairs, orderBook } = useSelector((s) => s.assets);
  const { data: user } = useSelector((s) => s.user);

  const [position, setPosition] = useState('Long');
  const [orderType, setOrderType] = useState('Market');
  const [marginType, setMarginType] = useState('Cross');
  const [leverage, setLeverage] = useState(10);
  const [limitPrice, setLimitPrice] = useState('');
  const [takeProfitPrice, setTakeProfitPrice] = useState('');
  const [stopLossPrice, setStopLossPrice] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);

  useEffect(() => {
    if (user?._id) dispatch(fetchFuturesBalances({ forceRefresh: true }));
  }, [dispatch, user?._id]);

  const tradingPair = useMemo(
    () => spotTradingPairs?.find((p) => p.symbol === selectedCoin) || null,
    [spotTradingPairs, selectedCoin]
  );

  const usdtBalance = futuresBalances.find((b) => b.asset_id?.symbol === 'USDT')?.balance || 0;

  const refPrice = useMemo(() => {
    const mkt = orderBook?.currentPrice || 0;
    return orderType === 'Limit' ? (parseFloat(limitPrice) || 0) : mkt;
  }, [orderBook?.currentPrice, orderType, limitPrice]);

  const qty = useMemo(() => parseFloat(amount) || 0, [amount]);
  const notional = useMemo(() => (refPrice && qty ? refPrice * qty : 0), [refPrice, qty]);
  const IM = useMemo(() => (notional && leverage ? notional / leverage : 0), [notional, leverage]);

  const canSubmit = useMemo(() => {
    if (!user?._id || !selectedCoin || !tradingPair) return false;
    if (!qty || qty <= 0) return false;
    if (orderType === 'Limit' && (!limitPrice || parseFloat(limitPrice) <= 0)) return false;
    return true;
  }, [user?._id, selectedCoin, tradingPair, qty, orderType, limitPrice]);

  const fillPct = (pct) => {
    if (!refPrice || !leverage) return;
    const targetIM = (usdtBalance * pct) / 100;
    const q = (targetIM * leverage) / refPrice;
    setAmount((+q).toFixed(4));
  };

  const validateBalance = () => {
    if (IM > usdtBalance) {
      setError(`الهامش المطلوب ${IM.toFixed(2)} USDT أكبر من رصيدك ${usdtBalance.toFixed(2)} USDT.`);
      return false;
    }
    return true;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setError(null);
    if (!canSubmit) return setError('أكمل الحقول بشكل صحيح.');
    if (!validateBalance()) return;
    setOpenConfirm(true);
  };

  const confirmTrade = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        trading_pair_id: tradingPair._id,
        position,
        margin_type: marginType,
        order_type: orderType,
        leverage: Number(leverage),
        amount: qty,
        limit_price: orderType === 'Limit' ? parseFloat(limitPrice) : null,
        take_profit_price: takeProfitPrice ? parseFloat(takeProfitPrice) : null,
        stop_loss_price: stopLossPrice ? parseFloat(stopLossPrice) : null,
      };
      await dispatch(createFutureTrade(payload)).unwrap();
      setAmount(''); setLimitPrice(''); setTakeProfitPrice(''); setStopLossPrice('');
      setError(null);
    } catch (err) {
      setError(typeof err === 'string' ? err : 'فشل إنشاء الصفقة');
    } finally {
      setIsSubmitting(false);
      setOpenConfirm(false);
    }
  };

  return (
    <Card component="form" onSubmit={onSubmit}>
      {/* رأس البطاقة */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <SectionTitle>Place Order — {selectedCoin || 'Select'}</SectionTitle>
        {isSubmitting && <LinearProgress sx={{ width: 120, height: 3, borderRadius: 99 }} />}
      </Box>

      {/* نوع الصفقة: لونج / شورت */}
      <ToggleButtonGroup
        value={position}
        exclusive
        onChange={(_e, v) => v && setPosition(v)}
        fullWidth
        sx={{
          mb: 1,
          '& .MuiToggleButton-root': { flex: 1, py: 1.1 },
          gap: 0.5,
        }}
      >
        <StyledToggle value="Long">Long</StyledToggle>
        <StyledToggle value="Short">Short</StyledToggle>
      </ToggleButtonGroup>

      {/* الهامش ونوع الأمر */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
        <FormControl size="small" fullWidth>
          <InputLabel>Margin</InputLabel>
          <Select
            label="Margin"
            value={marginType}
            onChange={(e) => setMarginType(e.target.value)}
            sx={selectSx}
          >
            <MenuItem value="Cross">Cross</MenuItem>
            <MenuItem value="Isolated">Isolated</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth>
          <InputLabel>Type</InputLabel>
          <Select
            label="Type"
            value={orderType}
            onChange={(e) => setOrderType(e.target.value)}
            sx={selectSx}
          >
            <MenuItem value="Market">Market</MenuItem>
            <MenuItem value="Limit">Limit</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* سلايدر الرافعة */}
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <SectionTitle>Leverage</SectionTitle>
          <Chip size="small" label={`${leverage}x`}
            sx={{ bgcolor: 'rgba(240,185,11,0.15)', color: '#b48703', fontWeight: 800 }} />
        </Box>
        <Slider
          min={1} max={100} step={1}
          value={leverage}
          onChange={(_e, v) => setLeverage(v)}
          marks={[{ value: 1, label: '1x' }, { value: 25, label: '25x' }, { value: 50, label: '50x' }, { value: 75, label: '75x' }, { value: 100, label: '100x' }]}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v}x`}
          sx={sliderSx}
        />
      </Box>

      {/* السعر (للـ Limit فقط) + الكمية */}
      <Box sx={{ display: 'grid', gridTemplateColumns: orderType === 'Limit' ? '1fr 1fr' : '1fr', gap: 1, mb: 1 }}>
        {orderType === 'Limit' && (
          <TextField
            size="small"
            label="Limit Price"
            type="number"
            placeholder="0.00"
            value={limitPrice}
            onChange={(e) => setLimitPrice(e.target.value)}
            sx={fieldSx}
            inputProps={{ inputMode: 'decimal', min: '0', step: 'any' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tag text="USDT" />
                </InputAdornment>
              ),
              sx: { pr: 1 }, // مساحة لوسم USDT
            }}
          />
        )}
        <TextField
          size="small"
          label="Amount"
          type="number"
          placeholder="0.0000"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          sx={fieldSx}
          inputProps={{ inputMode: 'decimal', min: '0', step: 'any' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tag text="Qty" />
              </InputAdornment>
            ),
            sx: { pr: 1 },
          }}
        />
      </Box>

      {/* أزرار نسبة لملء الكمية (تستخدم الرصيد وتقدير IM) */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 0.6, mb: 1 }}>
        {[25, 50, 75, 100].map((p) => (
          <Button
            key={p}
            size="small"
            variant="outlined"
            onClick={() => fillPct(p)}
            sx={{
              borderColor: COLORS.YELLOW,
              color: theme.palette.text.primary,
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': { borderColor: COLORS.YELLOW_DARK, background: 'rgba(240,185,11,0.08)' },
            }}
          >
            {p}%
          </Button>
        ))}
      </Box>

      {/* TP / SL — إزالة الأسهم + placeholder صغير + USDT */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
        <TextField
          size="small"
          label="Take Profit (optional)"
          type="number"
          placeholder="0.00"
          value={takeProfitPrice}
          onChange={(e) => setTakeProfitPrice(e.target.value)}
          sx={fieldSx}
          inputProps={{ inputMode: 'decimal', min: '0', step: 'any' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tag text="USDT" />
              </InputAdornment>
            ),
            sx: { pr: 1 },
          }}
        />
        <TextField
          size="small"
          label="Stop Loss (optional)"
          type="number"
          placeholder="0.00"
          value={stopLossPrice}
          onChange={(e) => setStopLossPrice(e.target.value)}
          sx={fieldSx}
          inputProps={{ inputMode: 'decimal', min: '0', step: 'any' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tag text="USDT" />
              </InputAdornment>
            ),
            sx: { pr: 1 },
          }}
        />
      </Box>

      {/* ملخص فوري */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        rowGap: 0.5, columnGap: 1,
        fontSize: 13, color: 'text.secondary', mb: 1,
      }}>
        <span>Price</span><span style={{ textAlign: 'right' }}>{refPrice ? refPrice.toFixed(2) : '--'} USDT</span>
        <span>Notional</span><span style={{ textAlign: 'right' }}>{notional ? notional.toFixed(2) : '--'} USDT</span>
        <span>Initial Margin</span><span style={{ textAlign: 'right' }}>{IM ? IM.toFixed(2) : '--'} USDT</span>
        <span>USDT Balance</span><span style={{ textAlign: 'right' }}>{usdtBalance.toFixed(2)} USDT</span>
      </Box>

      {error && (
        <Typography sx={{ color: theme.palette.error.main, fontSize: 13, mb: 1 }}>
          {error}
        </Typography>
      )}

      {/* زر التنفيذ */}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={!canSubmit || isSubmitting}
        sx={{
          py: 1,
          fontWeight: 800,
          letterSpacing: 0.3,
          borderRadius: 10,
          bgcolor: position === 'Long' ? COLORS.GREEN : COLORS.RED,
          '&:hover': { opacity: 0.92, bgcolor: position === 'Long' ? COLORS.GREEN : COLORS.RED },
          boxShadow: 'none',
        }}
      >
        {isSubmitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : `${position} ${orderType}`}
      </Button>

      {/* نافذة تأكيد */}
      <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)} fullWidth maxWidth="xs">
        <DialogTitle>Confirm Order</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">Pair: {selectedCoin}</Typography>
          <Typography variant="body2">Side: {position}</Typography>
          <Typography variant="body2">Type: {orderType}</Typography>
          <Typography variant="body2">Leverage: {leverage}x</Typography>
          {orderType === 'Limit' && <Typography variant="body2">Limit: {limitPrice}</Typography>}
          <Typography variant="body2">Amount: {amount}</Typography>
          {!!takeProfitPrice && <Typography variant="body2">TP: {takeProfitPrice}</Typography>}
          {!!stopLossPrice && <Typography variant="body2">SL: {stopLossPrice}</Typography>}
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">Est. IM: {IM.toFixed(2)} USDT</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirm(false)}>Cancel</Button>
          <Button
            onClick={confirmTrade}
            variant="contained"
            sx={{ bgcolor: COLORS.YELLOW, color: '#111', fontWeight: 800, '&:hover': { bgcolor: COLORS.YELLOW_DARK } }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
